import { Loader } from "@aws-amplify/ui-react";
import { memo, useEffect, useState } from "react";

// HOOKS
import { useAlert, useAuth, usePayment } from "src/hooks/context";
import { useAuthLink } from "src/hooks/useAuthLink";
import { useCreatePayment } from "src/hooks/useCreatePayment";
import { useEntityByNarwhal } from "src/hooks/useEntityByNarwhal";
import { useSetPaymentMethod } from "src/hooks/useSetPaymentMethod";
import { useTipping } from "src/hooks/useTipping";
import { useUpdatePayment } from "src/hooks/useUpdatePayment";

// COMPONENTS
import CamelBottom from "src/components/Layout/CamelBottom/CamelBottom";
import CamelTop from "src/components/Layout/CamelTop/CamelTop";
import { ChoosePaymentMethod } from "./ChoosePaymentMethod";

// ASSETS
import MerchantFallback from "../../assets/merchantFallback.png";

// STYLES
import { Auth } from "../Auth/Auth";
import BankList from "../BankList/BankList";
import TipModule from "../Tipping/TipModule";
import YourPayments from "../YourPayments/YourPayments";

type PayProps = {
    amount: string;
    recipient: Recipient;
    note: string;
    reference?: string;
    updateUrl?: (id: string) => void;
    goToPaymentMethodScreen?: (paymentId: string) => void;
    goToPaymentSummary: (foreignId: string) => void;
};

type Recipient = {
    id: string;
    name?: string;
    logo?: string;
};

const PayComponent = ({
    amount,
    recipient,
    note,
    reference,
    goToPaymentMethodScreen,
    goToPaymentSummary
}: PayProps) => {
    const { authState } = useAuth();
    const { paymentState, updatePaymentState } = usePayment();
    const { updateAlertState } = useAlert();

    const { createPayment } = useCreatePayment();
    const { updatePayment } = useUpdatePayment();
    const { setPaymentMethod } = useSetPaymentMethod();
    const { authLink } = useAuthLink();
    const { entityByNarwhal } = useEntityByNarwhal();
    const { handleTipSelect, activeTip, tips, tipAmount } = useTipping({
        initialAmount: amount,
    });

    const [originalNote] = useState(note);

    useEffect(() => {
        console.log({
            reference,
            amount,
            note,
            "paymentState.amount": paymentState.amount,
            recipient,
        })
        if (paymentState.amount === undefined) {
            updatePaymentState({
                id: reference ?? undefined,
                amount,
                recipient: recipient,
                note: decodeURI(note),
            });
        }
    }, [
        reference,
        amount,
        note,
        paymentState.amount,
        recipient,
        updatePaymentState,
    ]);

    useEffect(() => {
        const handleTipUpdate = async () => {
            if (!paymentState.recipient?.features?.hasTipping) {
                return;
            }
            if (tipAmount === undefined || tipAmount === paymentState.tipAmount) {
                return;
            }

            console.log("[Pay] paymentState.amount", paymentState.amount)
            console.log("[Pay] parseFloat(amount)", parseFloat(amount).toString())

            if (tipAmount === "0" && paymentState.amount === parseFloat(amount).toString()) {
                return;
            }

            let newAmount = parseFloat(amount);
            let updatedNote = note;

            let event = "TIP_ADDED";
            if (tipAmount === "0") {
                // No tip selected, revert to the original note
                newAmount = parseFloat(amount);
                updatedNote = originalNote;
                event = "TIP_REMOVED";
            } else {
                // Tip selected, add the tip to the amount and update the note
                newAmount += parseFloat(tipAmount);

                if (originalNote !== "pay") {
                    updatedNote = `${originalNote} (Tip: £${tipAmount})`;
                } else {
                    updatedNote = `(Tip: £${tipAmount})`;
                }
            }

            console.log("[Pay] amount", { amount })
            console.log("[Pay] new amount including tip", { newAmount })
            console.log("[Pay] tipAmount", { tipAmount })

            await updatePayment({
                paymentId: paymentState.id,
                amount: newAmount.toFixed(2).toString(),
                note: updatedNote.trim(),
                event
            })
            updatePaymentState({
                amount: newAmount.toFixed(2).toString(),
                tipAmount: tipAmount,
                paymentUrl: null,
                note: updatedNote.trim(),
            })
        }
        handleTipUpdate();
    }, [
        amount,
        handleTipSelect,
        note,
        originalNote,
        paymentState.amount,
        paymentState.id,
        paymentState.recipient?.features?.hasTipping,
        paymentState.tipAmount,
        tipAmount,
        updatePayment,
        updatePaymentState
    ]);

    useEffect(() => {
        async function getPayeeData() {
            if (!recipient) {
                return
            }
            if (paymentState.recipient?.id) {
                const payeeData = await entityByNarwhal({
                    narwhalId: recipient.id,
                });
                if (payeeData) {
                    updatePaymentState({
                        enabled: payeeData.enabled ?? undefined,
                        recipient: {
                            ...payeeData,
                            id: recipient.id,
                            logo: payeeData.logo ?? undefined,
                            features: {
                                guestCheckout:
                                    payeeData.features?.guestCheckout ??
                                    "ALWAYS_OFF",
                                hasCards: payeeData.features?.hasCards ?? false,
                                hasIBAN: payeeData.features?.hasIBAN ?? false,
                                hasTipping:
                                    payeeData.features?.hasTipping ?? false,
                                cardsSettings:
                                    payeeData.features?.cardsSettings,
                            },
                        },
                    });
                }
            }
        }
        getPayeeData();
    }, [
        recipient,
        paymentState.recipient?.id,
        entityByNarwhal,
        updatePaymentState,
    ]);

    useEffect(() => {
        async function createPaymentFn() {
            if (!authState.narwhalId || paymentState.id) {
                return;
            }
            // create the payment
            console.log("[Pay] note", paymentState.note);
            const createdPayment = await createPayment({
                amount: paymentState.amount || amount,
                payeeNarwhalId: recipient.id,
                note:
                    note !== "pay" || paymentState.note !== null
                        ? decodeURI(paymentState.note || note)
                        : undefined,
                payerNarwhalId: authState.narwhalId,
            });
            console.log({ createdPayment });
            if (createdPayment && createdPayment.id) {
                updatePaymentState({
                    ...createdPayment,
                    paymentUrl: null,
                });
            }
        }
        createPaymentFn();
    }, [
        amount,
        createPayment,
        note,
        paymentState.recipient?.id,
        paymentState.id,
        updatePaymentState,
        authState.narwhalId,
        paymentState.paymentMethod,
        paymentState.note,
        paymentState.amount,
        paymentState.paymentUrl,
        recipient.id,
        paymentState.recipient?.features?.hasTipping,
    ]);

    useEffect(() => {
        async function setPaymentMethodFn() {
            if (
                !paymentState.id ||
                !paymentState.paymentMethod ||
                !authState.narwhalId ||
                paymentState.paymentUrl
            ) {
                return;
            }
            // authLink endpoint
            // Better and faster
            // request the payment auth link from the bank
            // if (paymentState.transferId) {
            const bankAuthLink = await authLink({
                applicationUserId: authState.narwhalId,
                institutionId: paymentState.paymentMethod.id,
                payeeNarwhalId: recipient.id,
                payerNarwhalId: authState.narwhalId,
                paymentId: paymentState.id,
                paymentIdempotencyId: paymentState.id?.replaceAll(
                    "-",
                    ""
                ),
                transferId: paymentState.id,
            });
            if (bankAuthLink && bankAuthLink.link) {
                updatePaymentState({
                    paymentUrl: bankAuthLink.link,
                    amount: bankAuthLink.amount,
                    foreignId: bankAuthLink.foreignId,
                });
            }
            // }
        }
        setPaymentMethodFn();
    }, [
        setPaymentMethod,
        authLink,
        amount,
        note,
        paymentState.recipient?.id,
        paymentState.id,
        paymentState.transferId,
        paymentState.note,
        paymentState.amount,
        updatePaymentState,
        authState.narwhalId,
        paymentState.paymentMethod,
        paymentState.paymentUrl,
        recipient.id,
        updateAlertState,
    ]);

    const showAuth =
        authState &&
        authState.isLoggedIn === false &&
        paymentState.recipient?.features?.guestCheckout &&
        authState.isGuest === null;
    const showLoader =
        authState &&
        (authState.isLoggedIn === null ||
            !paymentState.recipient?.features?.guestCheckout) &&
        authState.isGuest === null;

    const cardNoStripe =
        paymentState.paymentMethod?.name === "Cards" &&
        !paymentState.recipient?.features?.hasCards;

    const showBankList =
        !authState.isTemporaryPassword &&
        // Guests are pseudo-users and always report signup-incomplete, so the
        // signup gate (TRI-2158) must only apply to authenticated users.
        (authState.isGuest === true ||
            authState.isSignupComplete !== false) &&
        ((paymentState.paymentMethod === undefined &&
            (authState.isLoggedIn === true || authState.isGuest === true) &&
            authState.narwhalId) ||
            (cardNoStripe && paymentState.paymentMethod?.name === "Cards"));

    const showSignupIncomplete =
        authState.isLoggedIn === true && authState.isSignupComplete === false;

    return (
        <>
            <CamelTop>
                {paymentState.amount && (
                    <YourPayments
                        name={
                            paymentState.recipient?.name ||
                            "You're good to go 👍"
                        }
                        icon={paymentState.recipient?.logo || MerchantFallback}
                        description={paymentState.note || "✌️"}
                        amount={`£${Number(
                            paymentState.amount || amount
                        ).toFixed(2)}`}
                        isPaymentProcessing={undefined}
                        logTitle="💸 You're paying"
                    />
                )}
                {paymentState.enabled === true && (
                    <>
                        {showLoader && (
                            <Loader size="large" variation="linear" />
                        )}
                        {showAuth && <Auth />}
                        {showSignupIncomplete && (
                            <div>
                                Your account registration is not complete. Please
                                complete your setup to continue.
                            </div>
                        )}
                        {showBankList && <BankList />}
                    </>
                )}
                {paymentState.enabled === false && (
                    <div
                        style={{
                            textAlign: "center",
                            color: "red",
                            fontSize: "1.6rem",
                        }}
                    >
                        ⚠️ Sorry, it looks like{" "}
                        {paymentState.recipient?.name ?? "this payee"}
                        <br />
                        can't receive your payment
                    </div>
                )}
            </CamelTop>
            <CamelBottom>
                {!showBankList &&
                    authState.narwhalId &&
                    paymentState.recipient?.features?.hasTipping &&
                    goToPaymentMethodScreen && (
                        <TipModule
                            isLoading={paymentState.paymentUrl === null}
                            amount={paymentState.amount || amount}
                            tips={tips}
                            activeTip={activeTip}
                            onTipSelect={handleTipSelect}
                        />
                    )}
                {goToPaymentMethodScreen &&
                    authState.narwhalId &&
                    !cardNoStripe && (
                        <ChoosePaymentMethod
                            authUrl={paymentState.paymentUrl}
                            goToPaymentSummary={goToPaymentSummary}
                        />
                    )}
            </CamelBottom>
        </>
    );
};

function areEqual(prevProps: any, nextProps: any) {
    if (
        prevProps.id === nextProps.id &&
        prevProps.amount === nextProps.amount &&
        prevProps.note === nextProps.note
    ) {
        return true;
    }
    return false;
}
export const Pay = memo(PayComponent, areEqual);
